import type { AnimationI, SimpleStyleT } from "tweenly"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import { useAnimationActions } from "../../../../hooks/useAnimationActions"
import PropertyRow from "../../../common/PropertyTools/PropertyRow"
import AnimationState from "./AnimationState"

interface Props {
    animation: AnimationI
    type: "fromTo" | "from" | "to" | "set"
}

type ColumnT = { state: "from" | "to"; title: string; styles: SimpleStyleT[] }

/** "clipPath" → "Clip path", "scale X" → "Scale X" — for a property without a label of its own. */
const toCaption = (text: string) => {
    const words = text.replace(/([a-z])([A-Z])/g, "$1 $2")
    return (
        words.charAt(0).toUpperCase() +
        words
            .slice(1)
            .toLowerCase()
            .replace(/ ([a-z])$/, (m) => m.toUpperCase())
    )
}

/**
 * A tween's states as a table: the columns are From and To (just one of them for a one-sided
 * tween), each row is one property with its start and end value side by side.
 */
const AnimationStates = ({ animation, type }: Props) => {
    const { updateAnimationTweenState } = useAnimationActions()

    const handleSetAnimationState = (e: any, state: "from" | "to") =>
        updateAnimationTweenState(animation, state, e.target.name, e.target.value)

    if (animation?.tween.name === "none") return <></>

    const from: ColumnT = {
        state: "from",
        // a "set" has no motion — its one state is simply what gets set
        title: type === "set" ? "Set" : "From",
        styles: animation.tween.from,
    }
    const to: ColumnT = { state: "to", title: "To", styles: animation.tween.to }
    const columns = type === "fromTo" ? [from, to] : type === "to" ? [to] : [from]

    // every property either state has, in the order they appear
    const properties = Array.from(
        new Set(columns.flatMap((column) => column.styles.map((style) => style.property)))
    )

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <PropertyRow>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                    {columns.map((column) => (
                        <Typography
                            key={column.state}
                            variant="caption"
                            sx={{ fontWeight: 600, color: "text.primary" }}
                        >
                            {column.title}
                        </Typography>
                    ))}
                </Box>
            </PropertyRow>
            {properties.map((property) => {
                const sample = columns
                    .map((column) => column.styles.find((style) => style.property === property))
                    .find(Boolean) as SimpleStyleT
                const caption = toCaption(sample.input.label ?? property)
                return (
                    <Box key={property}>
                        <PropertyRow>
                            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                                {columns.map((column) => {
                                    const style = column.styles.find(
                                        (item) => item.property === property
                                    )
                                    return (
                                        <Box key={column.state} minWidth={0}>
                                            {style && (
                                                <AnimationState
                                                    state={column.state}
                                                    animationState={style}
                                                    label={sample.input.label ?? property}
                                                    tooltip={`${caption} – ${column.title.toLowerCase()}`}
                                                    handleSetValue={handleSetAnimationState}
                                                />
                                            )}
                                        </Box>
                                    )
                                })}
                            </Box>
                        </PropertyRow>
                    </Box>
                )
            })}
        </Box>
    )
}

export default AnimationStates
